import React, { useState } from 'react';
import { Box, TextField, Button, Typography, Alert, Divider, Stack, Paper, Tabs, Tab } from '@mui/material';
import { orderService } from '../../../../shared/services/orders/orderService';
import { useNavigate } from 'react-router-dom';
import QrCode2Icon from '@mui/icons-material/QrCode2';
import { QrReader } from 'react-qr-reader';


interface OrderDeliverProps {
  orderId: string;
}

export function OrderDeliver({ orderId }: OrderDeliverProps) {
  const navigate = useNavigate();
  const [tab, setTab] = useState(0);

  // Manual
  const [deliveredBy, setDeliveredBy] = useState('');
  const [deliveredAt, setDeliveredAt] = useState('');
  const [deliveryDocument, setDeliveryDocument] = useState('');

  // PIN/Token
  const [pin, setPin] = useState('');
  const [token, setToken] = useState('');

  // QR Reader
  const [showQR, setShowQR] = useState(false);
  const [qrLoading, setQrLoading] = useState(false);

  // Feedback
  const [result, setResult] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handleManualDeliver = async () => {
    setError(null);
    setResult(null);
    try {
      await orderService.deliver(orderId, {
        deliveredBy,
        deliveredAt,
        deliveryDocument,
      });
      setResult('Encomenda marcada como entregue com sucesso!');
      setTimeout(() => navigate('/orders'), 1200);
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Erro ao entregar encomenda');
    }
  };

  const handlePickup = async () => {
    setError(null);
    setResult(null);
    try {
      const body = token ? { token } : { pin };
      const { data } = await orderService.pickup(body);
      setResult(`Encomenda retirada com sucesso: ${data.orderCode}`);
      setTimeout(() => navigate('/orders'), 1200);
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Erro ao retirar encomenda');
    }
  };

  // QR Code leitura automática
  const handleScan = (data: string | null) => {
    if (data && !qrLoading) {
      setQrLoading(true);
      // Extrai o token da URL do QR Code
      const tokenFromUrl = data.split('/').pop();
      setToken(tokenFromUrl || '');
      setTimeout(() => {
        setShowQR(false);
        setQrLoading(false);
      }, 500);
    }
  };

  const handleError = (err: any) => {
    // Não fecha a câmera, só mostra erro se quiser
    // setError('Erro ao ler QR Code');
  };

  return (
    <Paper elevation={0} sx={{ p: { xs: 2, md: 4 }, borderRadius: 3, bgcolor: '#FAFAFA' }}>
      <Tabs value={tab} onChange={(_, v) => setTab(v)} centered sx={{ mb: 3 }}>
        <Tab label="Entrega Manual" />
        <Tab label="Retirada por PIN/QR" />
      </Tabs>

      {tab === 0 && (
        <Box>
          <Typography fontWeight={600} mb={2}>Preencha os dados para marcar a encomenda como entregue:</Typography>
          <Stack spacing={2} mb={2}>
            <TextField
              label="Entregue por"
              value={deliveredBy}
              onChange={e => setDeliveredBy(e.target.value)}
              fullWidth
            />
            <TextField
              label="Data/Hora Entrega"
              type="datetime-local"
              value={deliveredAt}
              onChange={e => setDeliveredAt(e.target.value)}
              InputLabelProps={{ shrink: true }}
              fullWidth
            />
            <TextField
              label="Documento de Entrega"
              value={deliveryDocument}
              onChange={e => setDeliveryDocument(e.target.value)}
              fullWidth
            />
          </Stack>
          <Button
            variant="contained"
            color="primary"
            onClick={handleManualDeliver}
            disabled={!deliveredBy || !deliveredAt || !deliveryDocument}
            fullWidth
            sx={{ py: 1.5, fontWeight: 700, fontSize: 16, borderRadius: 2 }}
          >
            Marcar como Entregue
          </Button>
        </Box>
      )}

      {tab === 1 && (
        <Box>
          <Typography fontWeight={600} mb={2}>Retire a encomenda usando PIN ou QR Code:</Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} mb={2}>
            <TextField
              label="PIN de Retirada"
              value={pin}
              onChange={e => setPin(e.target.value)}
              fullWidth
            />
            <TextField
              label="Token do QR Code"
              value={token}
              onChange={e => setToken(e.target.value)}
              fullWidth
              InputProps={{
                endAdornment: (
                  <Button
                    startIcon={<QrCode2Icon />}
                    onClick={() => setShowQR(true)}
                    sx={{ minWidth: 0, px: 1 }}
                  >
                    Ler QR
                  </Button>
                )
              }}
            />
          </Stack>
          <Button
            variant="contained"
            color="secondary"
            onClick={handlePickup}
            disabled={!pin && !token}
            fullWidth
            sx={{ py: 1.5, fontWeight: 700, fontSize: 16, borderRadius: 2 }}
          >
            Retirar Encomenda
          </Button>
          {showQR && (
            <Box mt={3} display="flex" flexDirection="column" alignItems="center">
              <Typography mb={1}>Aponte a câmera para o QR Code:</Typography>
              <Box sx={{ border: '2px solid #eee', borderRadius: 2, overflow: 'hidden', mb: 2 }}>
                <Box sx={{ width: 260, height: 260, border: '2px solid #eee', borderRadius: 2, overflow: 'hidden' }}>
                  <QrReader
                    constraints={{ facingMode: 'environment' }}
                    onResult={(result, error) => {
                      if (result) handleScan(result.getText());
                      if (error) handleError(error);
                    }}
                  />
                </Box>
              </Box>
              {!qrLoading && (
                <Typography color="text.secondary" mb={1}>Aguardando QR Code...</Typography>
              )}
              <Button onClick={() => setShowQR(false)} sx={{ mt: 1 }} color="inherit" variant="outlined">
                Parar Câmera
              </Button>
            </Box>
          )}
        </Box>
      )}

      {result && <Alert severity="success" sx={{ mt: 3 }}>{result}</Alert>}
      {error && <Alert severity="error" sx={{ mt: 3 }}>{error}</Alert>}
    </Paper>
  );
}